import React, { useState, useEffect } from 'react'
import {
  Drawer,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { logout } from '../../api/auth/logout'
import localStorage from 'local-storage'
import largeLogo from '../../assets/images/rahatUp.png'
import smallLogo from '../../assets/images/rahatsistem-logo.png'

// Sidebar.dc.html tasarımından alınan sabit palet. Tema renklerine bağlı değil;
// tasarım koyu bir yeşil-lacivert şema tanımlıyor.
const COLORS = {
  sidebarBg: '#111d2c',
  border: '#1e2d40',
  logoGradient: 'linear-gradient(135deg, #22c55e, #0f766e)',
  byText: '#7dd3b8',
  brandText: '#f1f5f9',
  toggleIcon: '#94a3b8',
  collapsedToggleBg: '#16273a',
  active: '#34d399',
  inactive: '#94a3b8',
  activeBg: 'linear-gradient(90deg, rgba(16,185,129,0.18), rgba(16,185,129,0.03))',
  activeBorder: 'rgba(52,211,153,0.35)',
  sectionLabel: '#4b5b70',
  badgeGradient: 'linear-gradient(120deg,#22c55e,#10b981)',
  badgeText: '#06210f',
}

const svgBase = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none' }

// Tasarımdaki (Sidebar.dc.html) ICONS setinin birebir karşılığı.
const MonitorsIcon = () => (
  <svg {...svgBase}>
    <rect x="3" y="4" width="18" height="7" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <rect x="3" y="13" width="18" height="7" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="7" cy="7.5" r="0.9" fill="currentColor" />
    <circle cx="7" cy="16.5" r="0.9" fill="currentColor" />
  </svg>
)

const WrenchIcon = () => (
  <svg {...svgBase}>
    <path
      d="M14.7 6.3a4 4 0 0 0-5.4 4.7L3 17.3V21h3.7l6.3-6.3a4 4 0 0 0 4.7-5.4l-2.6 2.6-2-2 2.6-2.6z"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  </svg>
)

const UserIcon = () => (
  <svg {...svgBase}>
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M4.5 20c1.4-3.6 4.3-5.5 7.5-5.5s6.1 1.9 7.5 5.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
)

const CardIcon = () => (
  <svg {...svgBase}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

const LogoutIcon = () => (
  <svg {...svgBase}>
    <path
      d="M9 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M14 8l5 4-5 4M9 12h9.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const HamburgerIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const menuItems = [
  { text: 'Monitors', icon: <MonitorsIcon />, path: '/user/monitors', access: true },
  { text: 'Incidents', icon: <MonitorsIcon />, path: '/user/incidents', access: false },
  { text: 'Status Page', icon: <MonitorsIcon />, path: '/user/status-pages', access: false },
  { text: 'Maintenance', icon: <WrenchIcon />, path: '/user/maintenance', access: true },
  { text: 'Groups', icon: <UserIcon />, path: '/user/groups', access: false },
  { header: true, text: 'Account', access: true },
  { text: 'Profile', icon: <UserIcon />, path: '/user/profile', access: true },
  {
    text: 'Subscription',
    icon: <CardIcon />,
    path: '/user/subscription',
    badge: 'Pro',
    access: true,
  },
  { header: true, text: 'Other', access: true },
  { text: 'Log Out', icon: <LogoutIcon />, action: logout, path: '/login', access: true },
]

// `access: false` olan itemler henüz hazır değil (route + backend yok),
// menüde hiç gösterilmiyor.
const visibleMenuItems = menuItems.filter((item) => item.access !== false)

const ModernSidebar = ({ status, toggleSidebar }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  // `status` = sidebar sabitli mi (localStorage'da tutulan kalıcı durum).
  // `hovered` = mouse sidebar üzerindeyken geçici genişletme.
  const [hovered, setHovered] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Rota değişince mobil menüyü kapat
  useEffect(() => {
    if (isMobile) {
      setIsMobileOpen(false)
    }
  }, [location.pathname, isMobile])

  const handleItemClick = (item) => {
    if (item.action) {
      item.action()
    }
    if (item.path) {
      navigate(item.path)
    }
    if (isMobile) {
      setIsMobileOpen(false)
    }
  }

  const handleToggle = () => {
    toggleSidebar(!status)
    localStorage.set('sidebar', `${!status}`)
  }

  // `collapsed`, tasarımdaki değişkenin karşılığı. `status` = açık (expanded).
  // Mobilde her zaman genişletilmiş görünüm kullanılır.
  const renderContent = (collapsed) => {
    const sidePad = collapsed ? 12 : 16
    const itemPad = collapsed ? 0 : 12

    const renderItem = (item, index) => {
      // Bölüm başlığı — açık/kapalı durumda AYNI yüksekliği kaplar ki
      // altındaki menü öğeleri geçişte dikeyde kaymasın. İçerik dikeyde
      // ortalanır: açıkken yazı, kapalıyken ince ayraç çizgisi.
      if (item.header) {
        return (
          <Box
            key={`header-${index}`}
            sx={{
              height: 38,
              display: 'flex',
              alignItems: 'center',
              px: '4px',
            }}
          >
            {collapsed ? (
              <Box sx={{ width: '100%', height: '1px', background: COLORS.border, mx: '2px' }} />
            ) : (
              <Box
                component="span"
                sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: COLORS.sectionLabel,
                  textTransform: 'uppercase',
                }}
              >
                {item.text}
              </Box>
            )}
          </Box>
        )
      }

      const isActive = location.pathname === item.path

      return (
        <Box
          key={`item-${index}`}
          title={item.label || item.text}
          onClick={() => handleItemClick(item)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: '12px',
            // Sabit yükseklik: açık/kapalı fark etmez, yazının satır yüksekliği
            // ikondan farklı olsa da satır büyümez, alttaki öğeler kaymaz.
            height: 44,
            boxSizing: 'border-box',
            padding: `0 ${itemPad}px`,
            borderRadius: '10px',
            mb: '2px',
            cursor: 'pointer',
            color: isActive ? COLORS.active : COLORS.inactive,
            background: isActive ? COLORS.activeBg : 'transparent',
            border: `1px solid ${isActive ? COLORS.activeBorder : 'transparent'}`,
            transition: 'color 0.18s ease, background 0.18s ease',
            '&:hover': {
              color: COLORS.active,
              background: isActive ? COLORS.activeBg : 'rgba(148,163,184,0.06)',
            },
          }}
        >
          <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>
            {item.icon}
          </Box>
          {!collapsed && (
            <Box
              component="span"
              sx={{ fontSize: '15px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', flex: 1 }}
            >
              {item.text}
            </Box>
          )}
          {item.badge && !collapsed && (
            <Box
              component="span"
              sx={{
                fontSize: '11px',
                fontWeight: 800,
                color: COLORS.badgeText,
                background: COLORS.badgeGradient,
                padding: '3px 9px',
                borderRadius: '6px',
                flexShrink: 0,
              }}
            >
              {item.badge}
            </Box>
          )}
        </Box>
      )
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: COLORS.sidebarBg,
          overflow: 'hidden',
        }}
      >
        {/* Header — logo + (genişken) sabitleme butonu.
            İki logo çapraz geçişle (crossfade) değişir; ikisi de sol-üste
            sabitlendiği için genişleme sırasında kayma/zıplama olmaz. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            padding: `22px ${sidePad}px`,
            borderBottom: `1px solid ${COLORS.border}`,
            minHeight: 32,
            transition: 'padding 0.28s cubic-bezier(0.65,0,0.35,1)',
          }}
        >
          <Box sx={{ position: 'relative', height: 32, flex: '1 1 auto', minWidth: 0, overflow: 'hidden' }}>
            {/* Küçük logo: kapalıyken ortada, açılırken sola kayarak solar.
                Her iki logo da dikeyde ortalanır (top 50% + translateY) ki
                geçişte dikey kayma olmasın. */}
            <Box
              component="img"
              src={smallLogo}
              alt="RahatUp"
              sx={{
                position: 'absolute',
                top: '50%',
                left: collapsed ? '50%' : 0,
                height: 32,
                maxWidth: 'none',
                filter: 'brightness(0) invert(1)',
                opacity: collapsed ? 1 : 0,
                transform: collapsed ? 'translate(-50%, -50%)' : 'translate(0, -50%)',
                transition:
                  'left 0.28s cubic-bezier(0.65,0,0.35,1), transform 0.28s cubic-bezier(0.65,0,0.35,1), opacity 0.28s ease',
                pointerEvents: 'none',
              }}
            />
            <Box
              component="img"
              src={largeLogo}
              alt="RahatUp"
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                height: 32,
                maxWidth: 'none',
                filter: 'brightness(0) invert(1)',
                opacity: collapsed ? 0 : 1,
                transform: 'translateY(-50%)',
                transition: 'opacity 0.28s ease',
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}
            />
          </Box>
          {!isMobile && (
            <IconButton
              onClick={handleToggle}
              aria-label={status ? 'Sabitlemeyi kaldır' : 'Sidebar’ı sabitle'}
              title={status ? 'Sabitlemeyi kaldır' : 'Açık tut (sabitle)'}
              sx={{
                width: collapsed ? 0 : 32,
                height: 32,
                minWidth: 0,
                p: 0,
                borderRadius: '8px',
                color: status ? COLORS.active : COLORS.toggleIcon,
                background: status ? COLORS.collapsedToggleBg : 'transparent',
                flexShrink: 0,
                overflow: 'hidden',
                opacity: collapsed ? 0 : 1,
                visibility: collapsed ? 'hidden' : 'visible',
                transition: 'opacity 0.2s ease, width 0.2s ease',
                '&:hover': { background: COLORS.collapsedToggleBg, color: COLORS.brandText },
              }}
            >
              <HamburgerIcon />
            </IconButton>
          )}
        </Box>

        {/* Menü */}
        <Box
          sx={{
            flex: 1,
            padding: `16px ${sidePad}px`,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            overflowX: 'hidden',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              background: COLORS.border,
              borderRadius: 2,
            },
          }}
        >
          {visibleMenuItems.map(renderItem)}
        </Box>
      </Box>
    )
  }

  // Masaüstü kalıcı drawer.
  // Yer ayırma (reserved) genişliği yalnızca sabitli duruma (status) bağlı;
  // hover ile genişleme içeriği itmemesi için paper üstüne biner (overlay).
  if (!isMobile) {
    const isExpanded = status || hovered
    const reservedWidth = status ? 260 : 76
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: reservedWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isExpanded ? 260 : 76,
            boxSizing: 'border-box',
            background: COLORS.sidebarBg,
            borderRight: `1px solid ${COLORS.border}`,
            transition: 'width 0.28s cubic-bezier(0.65,0,0.35,1)',
            overflowX: 'hidden',
            // Daraltılmışken hover ile açıldığında içeriğin üzerine binsin
            boxShadow: isExpanded && !status ? '8px 0 24px rgba(0,0,0,0.35)' : 'none',
          },
        }}
        PaperProps={{
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }}
      >
        {renderContent(!isExpanded)}
      </Drawer>
    )
  }

  // Mobil geçici drawer (her zaman genişletilmiş içerik)
  return (
    <>
      <IconButton
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open menu"
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1300,
          color: COLORS.active,
          background: COLORS.sidebarBg,
          border: `1px solid ${COLORS.border}`,
          '&:hover': { background: COLORS.collapsedToggleBg },
        }}
      >
        <HamburgerIcon />
      </IconButton>

      <Drawer
        variant="temporary"
        open={isMobileOpen}
        onClose={() => setIsMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 260,
            boxSizing: 'border-box',
            background: COLORS.sidebarBg,
            borderRight: `1px solid ${COLORS.border}`,
          },
        }}
      >
        {renderContent(false)}
      </Drawer>
    </>
  )
}

export default ModernSidebar
